import m from "mithril";

const { Sortable } = require("sortablejs")
import Requester from "../../../shared/request";
import Gateway from "../../../shared/app_settings";
import i18n from "../../../shared/i18n/i18n";
import Toasts from "../../../shared/toasts";
import Auth from "../../../shared/auth";
import EditFieldToolbox, {
    hasUnsavedChanges as fieldToolboxHasUnsavedChanges,
    discardUnsavedChanges as discardFieldToolboxChanges,
    isOpen as fieldToolboxIsOpen,
    markClosed as markFieldToolboxClosed
} from "./EditFieldToolbox";
import EditSectionToolbox, {
    hasUnsavedChanges as sectionToolboxHasUnsavedChanges,
    discardUnsavedChanges as discardSectionToolboxChanges,
    isOpen as sectionToolboxIsOpen,
    markClosed as markSectionToolboxClosed
} from "./EditSectionToolbox";
import FormElementsMenu from "./FormElementsMenu";
import UndoHistory from "../shared/undo_history";
import UnsavedChanges from "../../../shared/unsaved_changes";
import {replaceInPlace} from "../shared/unit_state";
import sanitizeHtml from "sanitize-html";
import TextUtilities from "../../../shared/utilities/text_utilities";
import DropdownField from "../../../shared/components/component_dropdown_field";

let loading = false;
let saving = false;
let unit = null;
// The editor's content (see UndoHistory.serialise) as it was last loaded or saved.
// Kept as a string so the dirty check is a value comparison: the previous
// `unit === originalUnit` test compared against a deep clone and so was always
// true-dirty.
let originalUnit = null;
const history = [];

const saveForm = () => {
    saving = true;
    Requester.post(Gateway.admin + "units/assessment/" + unit.id + "/content", unit)
        .then(result => {
            // Take the server's copy without rebinding `unit`: the page holds
            // the same object and hasUnsavedChanges pins the navigation guard to
            // it by identity, so swapping in the response would leave the guard
            // permanently reporting "clean" and silently drop the ids the
            // backend assigned to newly added sections and fields.
            replaceInPlace(unit, result);
            // Re-baseline, otherwise the form stays dirty after every save and
            // the save button pulses for the rest of the session.
            originalUnit = UndoHistory.serialise(unit);
            Toasts.add("success", i18n.t("saved_changes"));
        })
        .catch(error => {
            Toasts.add("error", i18n.t("error"), i18n.t(error.response.properties.errorCode));
        })
        .then(() => {
            saving = false;
        });
};

const section = (id, name, information, fields, deps) => ({
    id: id || Math.random().toString(36).slice(2),
    name: name || "",
    information: information || "",
    prerequisiteOptionIds: deps || [],
    fields: fields || []
});

const field = (id, type, name, information, isRequired, options, deps) => ({
    id: id || Math.random().toString(36).slice(2),
    name: name || "",
    information: information || "",
    type: type || "textfield",
    isRequired: isRequired || false,
    prerequisiteOptionIds: deps || [],
    options: options || []
});

function initialiseFormSectionSorting(el) {
    // Sortable.create does not check for an existing instance: it overwrites its
    // own expando but leaves the previous instance's listeners bound, so one
    // drag would run the handler once per stale instance.
    if (!el || Sortable.get(el)) {
        return;
    }

    Sortable.create(el, {
        group: { name: "layout" },
        animation: 100,
        ghostClass: "dragging",
        onSort: evt => {
            let sectionItem;
            const isNew = evt.from.closest(".form-toolbox");

            UndoHistory.snapshot(history, unit);

            if (isNew) {
                sectionItem = section(null, i18n.t("new_section"));
            } else {
                const id = evt.item.getAttribute("id");
                for (let i = 0; i < unit.sections.length; i++) {
                    if (unit.sections[i].id === id) {
                        sectionItem = unit.sections[i];
                        unit.sections.splice(i, 1);
                    }
                }
            }

            unit.sections.splice(evt.newIndex, 0, sectionItem);

            if (isNew) {
                if (evt.item && evt.item.parentNode) {
                    evt.item.parentNode.removeChild(evt.item);
                }
                mountSectionToolbox(sectionItem, true);
            }

            m.redraw();
        }
    });
};

// Called from each section's oncreate, so it must only touch that section's
// own container. Initialising every container here would stack a new Sortable
// instance on the existing sections each time a section is added.
function initialiseFormFieldSorting(el) {
    if (!el || Sortable.get(el)) {
        return;
    }

    Sortable.create(el, {
        group: { name: "fields" },
        animation: 100,
        ghostClass: "dragging",
        onSort: evt => {
            let fieldItem;
            const isNew = evt.from.closest(".form-toolbox");

            UndoHistory.snapshot(history, unit);

            // If the field was added from the toolbox
            if (isNew) {
                const type = evt.item.getAttribute("data-type");
                fieldItem = field(null, type, i18n.t("new_field"));
            } else {
                const id = evt.item.getAttribute("id");
                for (let i = 0; i < unit.sections.length; i++) {
                    for (let x = 0; x < unit.sections[i].fields.length; x++) {
                        if (unit.sections[i].fields[x].id === id) {
                            fieldItem = unit.sections[i].fields[x];
                            unit.sections[i].fields.splice(x, 1);
                        }
                    }
                }
            }

            const fromSecId = evt.from.parentNode.getAttribute("id");
            const toSecId = evt.to.parentNode.getAttribute("id");

            // Nothing matched the dragged id, so there is nothing to place.
            // Without this the loop below splices `undefined` into the section.
            if (!fieldItem) {
                return;
            }

            // Add the field to the section
            for (let i = 0; i < unit.sections.length; i++) {
                if (unit.sections[i].id === toSecId) {
                    unit.sections[i].fields.splice(evt.newIndex, 0, fieldItem);
                }
            }

            if (isNew || fromSecId !== toSecId) {
                if (evt.item && evt.item.parentNode) {
                    evt.item.parentNode.removeChild(evt.item);
                }
            }

            // Show the field edit toolbox
            if (isNew) {
                mountFieldToolbox(fieldItem, true);
            }

            // Make sure the view is up to date
            m.redraw();
        }
    });
};

function undoLast() {
    if (UndoHistory.restore(history, unit)) {
        // The restored unit may not contain the section/field the toolbox is
        // editing, so drop back to the elements menu. The pending edits go with
        // it, so clear the toolbox modules too: their state is module-scoped and
        // would otherwise be reported against the next tab switch or route
        // change as edits that no longer exist.
        discardPendingToolboxChanges();
        m.mount(document.querySelector(".form-toolbox"), FormElementsMenu);
    }
};

// Mounts a toolbox, dropping whatever the previous one held. The section and
// field toolboxes keep separate module state, so mounting one over a dirty
// other would leave that other's flag set for good.
function showSectionToolbox(sec, isNew) {
    discardPendingToolboxChanges();
    m.mount(document.querySelector(".form-toolbox"), {
        view: () => m(EditSectionToolbox, { sec: sec, unit: unit, undoHistory: history, isNew: !!isNew })
    });
}

function mountSectionToolbox(s, isNew) {
    const sec = unit.sections.find(e => e.id === s.id);
    if (!sec) {
        return;
    }

    // Clicking away from a section or field that has unsaved edits is the loss
    // PLMS-2304 asks us to warn about, so confirm before swapping. Dragging in
    // a brand new element is deliberately not gated: onSort has already added
    // it to the unit, so cancelling would have nothing coherent to return to.
    if (!isNew && hasPendingToolboxChanges()) {
        UnsavedChanges.confirmDiscard(() => {
            showSectionToolbox(sec, isNew);
            m.redraw();
        });
        return;
    }

    showSectionToolbox(sec, isNew);
};

// Same swap semantics as showSectionToolbox.
function showFieldToolbox(fld, isNew) {
    discardPendingToolboxChanges();
    m.mount(document.querySelector(".form-toolbox"), {
        view: () => m(EditFieldToolbox, { fld: fld, unit: unit, undoHistory: history, isNew: !!isNew })
    });
}

function mountFieldToolbox(f, isNew) {
    let fld;
    for (let i = 0; i < unit.sections.length; i++) {
        for (let x = 0; x < unit.sections[i].fields.length; x++) {
            if (unit.sections[i].fields[x].id === f.id) {
                fld = unit.sections[i].fields[x];
            }
        }
    }
    if (!fld) {
        return;
    }

    // Clicking away from a section or field that has unsaved edits is the loss
    // PLMS-2304 asks us to warn about, so confirm before swapping. Dragging in
    // a brand new element is deliberately not gated: onSort has already added
    // it to the unit, so cancelling would have nothing coherent to return to.
    if (!isNew && hasPendingToolboxChanges()) {
        UnsavedChanges.confirmDiscard(() => {
            showFieldToolbox(fld, isNew);
            m.redraw();
        });
        return;
    }

    showFieldToolbox(fld, isNew);
};

function renderFieldInput(field) {
    if (field.type === "textfield") return m("input[type=text][disabled]", {placeholder: field.information});
    if (field.type === "textbox")   return m("textarea[disabled]", {placeholder: field.information});
    if (field.type === "email")     return m("input[type=email][disabled]", {placeholder: i18n.t("enter_email")});
    if (field.type === "number")    return m("input[type=number][disabled]", {placeholder: field.information});
    if (field.type === "date")      return m("input[type=text][disabled]", {placeholder: field.information});
    if (field.type === "checkbox") {
        return field.options.map(option => m(".checkbox-section",
            m(".div", m("i.icon-checkbox-empty text-gray"), m("span.ml-1", option.value))
        ));
    }
    if (field.type === "radio") {
        return field.options.map(option => m(".checkbox-section",
            m(".div", m(".radio-button text-gray"), m("span.ml-1", option.value))
        ));
    }
    if (field.type === "dropdownsingle" || field.type === "dropdownmulti") {
        return m(DropdownField, {field: field, readOnly: true});
    }
    return "";
}

function isUnitDirty() {
    if (unit === null || originalUnit === null) {
        return false;
    }

    return UndoHistory.serialise(unit) !== originalUnit;
}

// Lets the page register an unsaved-changes guard that stays in place while the
// user is on the settings/courses tabs, where this component is not mounted.
// forUnit pins the answer to the unit currently on screen, so leftover module
// state from a previously opened unit cannot report a false positive.
export function hasUnsavedChanges(forUnit) {
    return unit !== null && unit === forUnit && isUnitDirty();
}

// Edits sitting in the section/field toolbox are lost when the editor goes away,
// so the page can warn before switching tabs.
export function hasPendingToolboxChanges() {
    return sectionToolboxHasUnsavedChanges() || fieldToolboxHasUnsavedChanges();
}

export function discardPendingToolboxChanges() {
    discardSectionToolboxChanges();
    discardFieldToolboxChanges();
}

const Component_Unit_Assessment_Editor = {
    oninit: function (vnode) {
        const nextUnit = vnode.attrs.unit.properties;

        // Only reset when a different unit object is loaded. Switching between
        // the editor/settings/courses tabs remounts this component with the
        // same object, and must not discard the undo history or re-baseline the
        // dirty check (which would silently mark pending edits as saved).
        if (unit !== nextUnit) {
            UndoHistory.clear(history);
            originalUnit = UndoHistory.serialise(nextUnit);
        }

        unit = nextUnit;

        // The toolboxes are mounted on a root of their own, so tearing this
        // tree down (a tab switch) removes the node without running onremove.
        // The view below remounts the elements menu, so neither editor is open
        // whatever the flags were left holding.
        markFieldToolboxClosed();
        markSectionToolboxClosed();
    },
    view: function () {
        const currentUnit = unit ? UndoHistory.serialise(unit) : null;
        const isDirty = isUnitDirty();
        const canUndo = UndoHistory.canUndo(history, unit, currentUnit);
        // Both toolboxes edit a copy that only reaches the unit on "save
        // field"/"save section", so saving from here while one is open would
        // drop those edits.
        const isEditingElement = fieldToolboxIsOpen() || sectionToolboxIsOpen();
        const canSave = isDirty && !saving && !isEditingElement;

        return [
            m(".content-box", [
                !loading ? [
                    unit ? [m(".flex-row",[
                        Auth.validateRights(["unit_form_edit"], [
                            m(".toolbar mb-2", [
                                m(".toolbar-section", [
                                    m("button.with-icon btn-cta", {
                                        onclick: saveForm,
                                        disabled: !canSave,
                                        // Draw attention to unsaved changes.
                                        class: canSave ? "btn-pulse" : "disabled"
                                    }, [m("i.icon-check"), i18n.t("save_checklist")]),
                                    m("button.with-icon btn-error", {
                                        onclick: undoLast,
                                        disabled: !canUndo,
                                        class: canUndo ? "" : "disabled"
                                    }, [m("i.icon-undo"), i18n.t("undo_last")])
                                ])
                            ])
                        ])
                    ]),
                        m(".flex-row",[
                            (!isDirty || saving) ? "" : m(".form-toolbox-container.text-error.my-2",
                                [m("i.icon-warning"), m("span.mx-2", i18n.t("unsaved_form_changes"))] ,
                            ),
                        ])]
                    : "",

                    unit ? m(".flex-row", [
                        m(".form-toolbox-container", { style: "width: 30%; max-width: 400px; min-width: 300px;" }, [
                            m(".form-toolbox", m(FormElementsMenu))
                        ]),
                        m(".flex-auto flex-column ml-2", [
                            m("h3.text-center", i18n.t("assessment_form_editor")),
                            m(".form-unit form-preview", [
                                m(".mb-2", [
                                    m(".text-bold", unit.name),
                                    TextUtilities.hasVisibleText(unit.instructions) ? m("div.mb-2.instructions", m.trust(sanitizeHtml(unit.instructions || "",
                                        {
                                            allowedTags: sanitizeHtml.defaults.allowedTags, allowedAttributes:
                                                {"*": ["style", "class"], "a": ["href", "target"]}
                                        }))
                                    ) : "",
                                ]),
                                m(".form-sections form full-width", {
                                    oncreate: Auth.validateRights(["unit_form_edit"], vnode => initialiseFormSectionSorting(vnode.dom))
                                }, [
                                    unit.sections.length > 0 ? unit.sections.map(section => m(".form-section mb-2 full-width", {
                                        key: section.id,
                                        id: section.id,
                                        onclick: () => mountSectionToolbox(section)
                                    }, [
                                        m(".form-label.text-bold.richtext", m.trust(sanitizeHtml(section.name || "", {allowedTags: sanitizeHtml.defaults.allowedTags, allowedAttributes: {"*": ["style", "class"], "a": ["href", "target"]}}))),
                                        section.information ? m(".form-subtitle.richtext.mb-3", m.trust(sanitizeHtml(section.information || "", {allowedTags: sanitizeHtml.defaults.allowedTags, allowedAttributes: {"*": ["style", "class"], "a": ["href", "target"]}}))) : "",
                                        m(".form form-section-fields full-width", {
                                            oncreate: Auth.validateRights(["unit_form_edit"], vnode => initialiseFormFieldSorting(vnode.dom))
                                        }, [
                                            section.fields.map(field => m(".form-section", {
                                                key: field.id,
                                                id: field.id,
                                                // Mirror the learner view so the red
                                                // asterisk marks required fields here too.
                                                class: field.isRequired ? "required" : "",
                                                onclick: e => {
                                                    e.stopPropagation();
                                                    mountFieldToolbox(field);
                                                }
                                            }, [
                                                m(".form-label.richtext", m.trust(field.name || "")),
                                                renderFieldInput(field)
                                            ]))
                                        ])
                                    ])) : m(".text-gray", i18n.t("drag_section_here"))
                                ])
                            ])
                        ])
                    ]) : m(".text-gray padding", i18n.t("unable_to_load_unit"))
                ] : m(".text-gray padding", [i18n.t("loading"), m(".loading-dots")])
            ])
        ];
    }
};

export default Component_Unit_Assessment_Editor;
